import React, { useState, useEffect } from "react";
import axios from "axios";
import AdminLayout from "@/components/AdminLayout";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Save, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    order: 0,
    active: true,
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/categories`, {
        params: { active_only: false },
        headers: { Authorization: `Bearer ${token}` },
      });
      setCategories(response.data.sort((a, b) => a.order - b.order));
    } catch (error) {
      console.error("Errore nel caricamento delle categorie:", error);
      toast.error("Errore nel caricamento delle categorie");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : name === "order" ? parseInt(value) : value,
    });
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("admin_token");
      await axios.post(`${API}/categories`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Categoria aggiunta");
      setShowAddForm(false);
      setFormData({ name: "", order: 0, active: true });
      fetchCategories();
    } catch (error) {
      console.error("Errore nell'aggiunta della categoria:", error);
      toast.error("Errore nell'aggiunta della categoria");
    }
  };

  const handleEdit = (category) => {
    setEditingId(category.id);
    setFormData({
      name: category.name,
      order: category.order,
      active: category.active,
    });
  };

  const handleUpdate = async (categoryId) => {
    try {
      const token = localStorage.getItem("admin_token");
      await axios.put(`${API}/categories/${categoryId}`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Categoria aggiornata");
      setEditingId(null);
      fetchCategories();
    } catch (error) {
      console.error("Errore nell'aggiornamento della categoria:", error);
      toast.error("Errore nell'aggiornamento della categoria");
    }
  };

  const handleDelete = async (categoryId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questa categoria?")) return;

    try {
      const token = localStorage.getItem("admin_token");
      await axios.delete(`${API}/categories/${categoryId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Categoria eliminata");
      fetchCategories();
    } catch (error) {
      console.error("Errore nell'eliminazione della categoria:", error);
      toast.error("Errore nell'eliminazione della categoria");
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setFormData({ name: "", order: 0, active: true });
  };

  if (loading) {
    return (
      <AdminLayout title="Categorie">
        <div className="flex justify-center py-12">
          <div className="inline-block w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Categorie">
      <div data-testid="admin-categories">
      {/* Add Button */}
      <div className="mb-6">
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="bg-green-500 text-black px-6 py-3 rounded-lg font-bold hover:bg-green-600 transition-all inline-flex items-center gap-2"
          data-testid="add-category-button"
        >
          <Plus className="w-5 h-5" />
          Aggiungi categoria
        </button>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 mb-6" data-testid="add-category-form">
          <h2 className="text-2xl font-bold text-white mb-4">Nuova categoria</h2>
          <form onSubmit={handleAdd} className="space-y-4">
            <div>
              <label className="block text-white font-semibold mb-2" htmlFor="name">
                Nome
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-green-500"
                required
                data-testid="category-name-input"
              />
            </div>
            <div>
              <label className="block text-white font-semibold mb-2" htmlFor="order">
                Ordinamento
              </label>
              <input
                type="number"
                id="order"
                name="order"
                value={formData.order}
                onChange={handleInputChange}
                className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-green-500"
                data-testid="category-order-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="active"
                name="active"
                checked={formData.active}
                onChange={handleInputChange}
                className="w-5 h-5"
                data-testid="category-active-checkbox"
              />
              <label htmlFor="active" className="text-white font-semibold">
                Attiva
              </label>
            </div>
            <div className="flex gap-3">
              <button
                type="submit"
                className="bg-green-500 text-black px-6 py-3 rounded-lg font-bold hover:bg-green-600 transition-all"
                data-testid="save-category-button"
              >
                Salva
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowAddForm(false);
                  setFormData({ name: "", order: 0, active: true });
                }}
                className="bg-zinc-800 text-white px-6 py-3 rounded-lg font-bold hover:bg-zinc-700 transition-all"
                data-testid="cancel-add-button"
              >
                Annulla
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Categories List */}
      <div className="space-y-4" data-testid="categories-list">
        {categories.length === 0 ? (
          <div className="text-center py-12 bg-zinc-900 border border-zinc-800 rounded-lg">
            <p className="text-zinc-400">Nessuna categoria presente</p>
          </div>
        ) : (
          categories.map((category) => (
            <div
              key={category.id}
              className="bg-zinc-900 border border-zinc-800 rounded-lg p-6"
              data-testid={`category-${category.id}`}
            >
              {editingId === category.id ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-white font-semibold mb-2">Nome</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-green-500"
                      data-testid="edit-category-name"
                    />
                  </div>
                  <div>
                    <label className="block text-white font-semibold mb-2">Ordinamento</label>
                    <input
                      type="number"
                      name="order"
                      value={formData.order}
                      onChange={handleInputChange}
                      className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-green-500"
                      data-testid="edit-category-order"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      name="active"
                      checked={formData.active}
                      onChange={handleInputChange}
                      className="w-5 h-5"
                      data-testid="edit-category-active"
                    />
                    <label className="text-white font-semibold">Attiva</label>
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleUpdate(category.id)}
                      className="bg-green-500 text-black px-4 py-2 rounded-lg font-bold hover:bg-green-600 transition-all inline-flex items-center gap-2"
                      data-testid="save-edit-button"
                    >
                      <Save className="w-4 h-4" />
                      Salva
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="bg-zinc-800 text-white px-4 py-2 rounded-lg font-bold hover:bg-zinc-700 transition-all inline-flex items-center gap-2"
                      data-testid="cancel-edit-button"
                    >
                      <X className="w-4 h-4" />
                      Annulla
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-white mb-1" data-testid="category-name">{category.name}</h3>
                    <p className="text-zinc-400 text-sm">
                      Ordinamento: {category.order} | Stato: {category.active ? "Attiva" : "Non attiva"}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(category)}
                      className="p-3 bg-yellow-500 text-black rounded-lg hover:bg-yellow-600 transition-all"
                      data-testid="edit-category-button"
                    >
                      <Edit className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(category.id)}
                      className="p-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all"
                      data-testid="delete-category-button"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
      </div>
    </AdminLayout>
  );
};

export default AdminCategories;
